import { DialogContent, DialogOverlay } from '@reach/dialog'
import { transparentize } from 'polished'
import React from 'react'
import { animated, useSpring, useTransition } from 'react-spring'
import { useGesture } from 'react-use-gesture'
import styled, { css } from 'styled-components/macro'

import { isMobile } from '../../utils/userAgent'

const AnimatedDialogOverlay = animated(DialogOverlay)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StyledDialogOverlay = styled(AnimatedDialogOverlay)<{
  secondaryPopup: boolean
  flexWrap?: string
  alignContent?: string
}>`
  &[data-reach-dialog-overlay] {
    z-index: 2;
    background-color: transparent;
    overflow: hidden;
    display: flex;
    flex-direction: ${({ theme, secondaryPopup }) => (secondaryPopup ? 'column' : 'row')};
    align-items: center;
    justify-content: center;
    flex-wrap: ${({ flexWrap }) => flexWrap};
    align-content: ${({ alignContent }) => alignContent};
    background-color: ${({ theme }) => theme.modalBG};
  }
`

const AnimatedDialogContent = animated(DialogContent)
// destructure to not pass custom props to Dialog DOM element
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StyledDialogContent = styled(
  ({ minHeight, maxHeight, mobile, mobileModalPosition, hideBorder, isOpen, modalWidth, backgroundColor, ...rest }) => (
    <AnimatedDialogContent {...rest} />
  )
).attrs({
  'aria-label': 'dialog',
})`
  overflow-y: auto;

  &[data-reach-dialog-content] {
    margin: ${({ theme, modalWidth }) => (modalWidth ? '0 0 1rem 0' : '0 0 2rem 0')};
    background-color: ${({ theme, backgroundColor }) => (backgroundColor ? backgroundColor : theme.bg0)};
    border: ${({ theme, hideBorder }) => (hideBorder ? 'none' : '1px solid ' + theme.bg1)};
    box-shadow: 0 4px 8px 0 ${({ theme }) => transparentize(0.95, theme.shadow1)};
    padding: 0px;
    width: 50vw;
    overflow-y: auto;
    overflow-x: hidden;

    align-self: ${({ mobile, mobileModalPosition }) =>
      mobile ? (mobileModalPosition === 'TOP' ? 'start' : 'flex-end') : 'center'};

    max-width: ${({ theme, modalWidth }) => (modalWidth ? modalWidth : '420px')};
    ${({ maxHeight }) =>
      maxHeight &&
      css`
        max-height: ${maxHeight}vh;
      `}
    ${({ minHeight }) =>
      minHeight &&
      css`
        min-height: ${minHeight}vh;
      `}
    display: flex;
    border-radius: 20px;
    ${({ theme }) => theme.mediaWidth.upToMedium`
      width: 65vw;
      margin: 0;
    `}
    ${({ theme, mobile, mobileModalPosition }) => theme.mediaWidth.upToSmall`
      width:  85vw;
      ${
        mobile &&
        css`
          width: 100vw;
          border-radius: 20px;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        `
      }
      ${
        mobileModalPosition === 'TOP' &&
        css`
          width: 100vw;
          border-radius: unset;
          border: none;
        `
      }
    `}
  }
`

interface ModalProps {
  isOpen: boolean
  onDismiss: () => void
  minHeight?: number | false
  maxHeight?: number
  initialFocusRef?: React.RefObject<any>
  children?: React.ReactNode
  mobileModalPosition?: 'TOP' | 'BOTTOM'
  hideBorder?: boolean
  secondaryPopup?: () => JSX.Element
  modalWidth?: string
  backgroundColor?: string
  flexWrap?: string
  alignContent?: string
}

export default function Modal({
  isOpen,
  onDismiss,
  minHeight = false,
  maxHeight = 90,
  initialFocusRef,
  children,
  mobileModalPosition = 'BOTTOM',
  hideBorder = false,
  secondaryPopup,
  modalWidth,
  backgroundColor,
  flexWrap,
  alignContent,
}: ModalProps) {
  const fadeTransition = useTransition(isOpen, null, {
    config: { duration: 200 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  })

  const [{ y }, set] = useSpring(() => ({ y: 0, config: { mass: 1, tension: 210, friction: 20 } }))
  const bind = useGesture({
    onDrag: (state) => {
      set({
        y: state.down ? state.movement[1] : 0,
      })
      if (state.movement[1] > 300 || (state.velocity > 3 && state.direction[1] > 0)) {
        onDismiss()
      }
    },
  })

  return (
    <>
      {fadeTransition.map(
        ({ item, key, props }) =>
          item && (
            <StyledDialogOverlay
              key={key}
              style={props}
              onDismiss={onDismiss}
              initialFocusRef={initialFocusRef}
              unstable_lockFocusAcrossFrames={false}
              secondaryPopup={!!secondaryPopup}
              flexWrap={flexWrap}
              alignContent={alignContent}
            >
              <StyledDialogContent
                {...(isMobile
                  ? {
                      ...bind(),
                      style: { transform: y.interpolate((y) => `translateY(${(y as number) > 0 ? y : 0}px)`) },
                    }
                  : {})}
                aria-label="dialog content"
                minHeight={minHeight}
                maxHeight={maxHeight}
                mobile={isMobile}
                mobileModalPosition={mobileModalPosition}
                hideBorder={hideBorder}
                modalWidth={modalWidth}
                backgroundColor={backgroundColor}
              >
                {/* prevents the automatic focusing of inputs on mobile by the reach dialog */}
                {!initialFocusRef && isMobile ? <div tabIndex={1} /> : null}
                {children}
              </StyledDialogContent>
              {secondaryPopup && (
                <StyledDialogContent
                  {...(isMobile
                    ? {
                        ...bind(),
                        style: { transform: y.interpolate((y) => `translateY(${(y as number) > 0 ? y : 0}px)`) },
                      }
                    : {})}
                  aria-label="dialog content"
                  minHeight={minHeight}
                  maxHeight={maxHeight}
                  mobile={isMobile}
                  mobileModalPosition={mobileModalPosition}
                  hideBorder={hideBorder}
                  modalWidth={modalWidth}
                  backgroundColor={backgroundColor}
                >
                  {/* prevents the automatic focusing of inputs on mobile by the reach dialog */}
                  {!initialFocusRef && isMobile ? <div tabIndex={1} /> : null}
                  {secondaryPopup()}
                </StyledDialogContent>
              )}
            </StyledDialogOverlay>
          )
      )}
    </>
  )
}
